import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { ArticleService } from "../../services/articleServic";

const AddJournal = ({
  isOpen,
  onCancel,
  documentId,
  projectId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    journalName: "",
    link: "",
    quartile: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (loading) return;

    setFormData({
      journalName: "",
      link: "",
      quartile: "",
    });

    onCancel?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectId || !documentId) {
      toast.error("Project ID and document ID are required.");
      return;
    }

    if (
      !formData.journalName.trim() ||
      !formData.link.trim() ||
      !formData.quartile
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        documentId,
        projectId,
        journalName: formData.journalName.trim(),
        link: formData.link.trim(),
        quartile: formData.quartile,
      };

      const res = await ArticleService.addJournals(payload);

      if (res?.success === false) {
        throw new Error(res?.message || "Failed to add journal.");
      }

      toast.success(res?.message || "Journal added successfully.");

      setFormData({
        journalName: "",
        link: "",
        quartile: "",
      });

      onCancel?.();
      onSuccess?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add journal."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) {
          handleClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-journal-title"
        className="my-auto w-full max-w-lg rounded-2xl bg-white shadow-2xl"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 sm:px-6">
          <div>
            <h2
              id="add-journal-title"
              className="text-lg font-bold text-gray-800"
            >
              Add New Journal
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Enter journal details below.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Journal Name <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              name="journalName"
              value={formData.journalName}
              onChange={handleChange}
              placeholder="Enter journal name"
              maxLength={255}
              required
              className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Journal Link <span className="text-red-500">*</span>
            </label>

            <input
              type="url"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="https://example.com/journal"
              maxLength={1000}
              required
              className="w-full rounded-lg border border-gray-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Quartile <span className="text-red-500">*</span>
            </label>

            <select
              name="quartile"
              value={formData.quartile}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
            >
              <option value="" disabled>
                Select Quartile
              </option>
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
              <option value="Q3">Q3</option>
              <option value="Q4">Q4</option>
            </select>
          </div>

          {/* Modal Footer */}
          <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={17} />
              {loading ? "Saving..." : "Add Journal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddJournal;
